import { useState, type ReactNode } from 'react'
import { Box, Collapse, IconButton, Typography } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { FS_SM, ICON_SM } from '../common/theme'

interface EvalCategorySectionProps {
  name: string
  children?: ReactNode
  // Optional right-side header content, e.g. TaPassFail counts with an
  // arrow for the compare view. Sits between the title and the expand
  // toggle.
  headerExtras?: ReactNode
  // Left rail tint. `success.main` / `error.main` / `warning.main`
  // in scored mode (drives the pass/fail balance signal); plain
  // `divider` for improvements where there's no scoring signal.
  railColor?: string
  defaultExpanded?: boolean
}

// Shared shell for a checkpoint category card — used by both the
// scored evaluation (TaEvalScored) and the improvements view
// (TaEvalImprovements). Owns the rail, header strip, expand/collapse
// state and Collapse animation; the caller supplies the per-row
// children and any header extras.
export default function EvalCategorySection({
  name, children, headerExtras, railColor = 'divider', defaultExpanded = true,
}: EvalCategorySectionProps) {
  const [showItems, setShowItems] = useState(defaultExpanded)
  return (
    <Box sx={{ borderLeft: 2, borderColor: railColor, mb: 0.5 }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 0.5,
        px: 1, py: 0.4,
        bgcolor: `#0000001B`,
      }}>
        <Typography variant="caption" sx={{ flex: 1, fontWeight: 600, fontSize: FS_SM }}>
          {name}
        </Typography>
        {headerExtras}
        <IconButton size="small" onClick={() => setShowItems(v => !v)} sx={{ p: 0.2 }}>
          {showItems ? <ExpandLessIcon sx={{ fontSize: ICON_SM }} /> : <ExpandMoreIcon sx={{ fontSize: ICON_SM }} />}
        </IconButton>
      </Box>
      <Collapse in={showItems}>
        {children}
      </Collapse>
    </Box>
  )
}
